
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { GallerySection, ImageData } from "./types";
import { getImageUrl } from "./utils";
import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { toast } from "sonner";

interface GalleryCardProps {
  gallery: GallerySection;
  sectionImages: ImageData[];
  maxHeight?: string;
  onImageUpload?: () => void;
}

export const GalleryCard = ({ gallery, sectionImages, maxHeight, onImageUpload }: GalleryCardProps) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const isMobile = useIsMobile();

  const renderContent = () => {
    if (gallery.isVideoGallery && gallery.videos) {
      return (
        <CarouselContent className="h-full">
          {gallery.videos.map((video, index) => (
            <CarouselItem key={`video-${index}`} className="flex flex-col items-center justify-center" style={{ height: maxHeight || "400px" }}>
              <div className="w-full h-full max-w-3xl mx-auto flex flex-col">
                <div className="flex-grow aspect-video w-full flex items-center">
                  <iframe
                    src={video.url}
                    title={video.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full rounded-lg border-2 border-white/20"
                  />
                </div>
                <h4 className="text-white text-sm font-semibold mt-4 text-center uppercase">{video.title}</h4>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
      );
    }

    const imageUrls = sectionImages.length > 0
      ? sectionImages.map(img => ({ url: getImageUrl(img), id: img.id })).filter(img => img.url)
      : gallery.fallbackImages.map((url, i) => ({ url, id: `fallback-${i}` }));

    return (
      <CarouselContent>
        {imageUrls.map((image, imageIndex) => (
          <CarouselItem key={image.id} className="flex items-center justify-center">
            <div className="aspect-w-4 aspect-h-3 relative w-full" style={{ maxHeight: maxHeight }}>
              <img
                src={image.url}
                alt={`${gallery.title} ${imageIndex + 1}`}
                className="rounded-lg object-cover w-full h-full"
              />
            </div>
          </CarouselItem>
        ))}
      </CarouselContent>
    );
  };

  const carouselOptions = {
    align: "center" as const,
    startIndex: currentImageIndex,
    dragFree: true,
    draggable: true,
    loop: true
  };

  const handleImageUploadComplete = () => {
    if (onImageUpload) {
      onImageUpload();
    }
    toast.success("Bild erfolgreich hochgeladen! Es wird gleich angezeigt.");
  };

  return (
    <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg shadow-lg border border-white/20">
      {gallery.isVideoGallery ? (
        <>
          <h3 className="text-white text-base lg:text-lg font-bold uppercase mb-6 text-center">LIEBLINGSLIEDER</h3>
          <Carousel 
            className="w-full"
            opts={carouselOptions}
          >
            {renderContent()}
            <div className="flex justify-between mt-4">
              <CarouselPrevious className="static transform-none h-auto w-auto bg-transparent border-none text-white hover:bg-transparent hover:text-white/80 focus:bg-transparent">
                <div className="flex items-center">
                  <ChevronLeft className="w-4 h-4" />
                  <ChevronLeft className="w-4 h-4 -ml-2" />
                </div>
              </CarouselPrevious>
              <CarouselNext className="static transform-none h-auto w-auto bg-transparent border-none text-white hover:bg-transparent hover:text-white/80 focus:bg-transparent">
                <div className="flex items-center">
                  <ChevronRight className="w-4 h-4" />
                  <ChevronRight className="w-4 h-4 -ml-2" />
                </div>
              </CarouselNext>
            </div>
          </Carousel>
          
          {/* Upload-Bereich auskommentiert
          <div className="mt-4 pt-4 border-t border-white/20">
            <h4 className="text-sm font-semibold text-white mb-2">{gallery.sectionTitle}</h4>
            <ImageUpload 
              defaultSection="passions"
              defaultSubsection={gallery.uploadSection} 
            />
          </div>
          */}
        </>
      ) : (
        <>
          <h3 className="text-white text-base lg:text-lg font-bold uppercase mb-6 text-center">LEIDENSCHAFTEN</h3>
          <Carousel 
            className="w-full"
            opts={carouselOptions}
          >
            {renderContent()}
            <div className="flex justify-between mt-4">
              <CarouselPrevious className="static transform-none h-auto w-auto bg-transparent border-none text-white hover:bg-transparent hover:text-white/80 focus:bg-transparent">
                <div className="flex items-center">
                  <ChevronLeft className="w-4 h-4" />
                  <ChevronLeft className="w-4 h-4 -ml-2" />
                </div>
              </CarouselPrevious>
              <CarouselNext className="static transform-none h-auto w-auto bg-transparent border-none text-white hover:bg-transparent hover:text-white/80 focus:bg-transparent">
                <div className="flex items-center">
                  <ChevronRight className="w-4 h-4" />
                  <ChevronRight className="w-4 h-4 -ml-2" />
                </div>
              </CarouselNext>
            </div>
          </Carousel>
          
          {/* Upload-Bereich auskommentiert
          <div className="mt-4 pt-4 border-t border-white/20">
            <h4 className="text-sm font-semibold text-white mb-2">{gallery.sectionTitle}</h4>
            <ImageUpload 
              defaultSection="passions"
              defaultSubsection={gallery.uploadSection} 
            />
          </div>
          */}
        </>
      )}
    </div>
  );
};
